import React, { useState, useMemo, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import pathfinder from '@fiverr-private/pathfinder';
import { I18n } from '@fiverr-private/i18n-react';
import { triggerSignIn } from '@fiverr-private/identification_triggers';
import { breakpointSm, breakpointMd, breakpointReverseSm, breakpointReverseMd } from '@fiverr-private/sass/helpers';
import { makersExperience } from '@fiverr-private/logo_maker_analytics';
import { setVariationTextFields } from '@fiverr-private/variations_display';
import { VARIATION_TYPE } from '../constants';
import { useUserAgentContext } from '../../../../../shared/components/UserAgentContext';
import { useEffectOnce, useTopLevelSource } from '../../../../../shared/hooks';
import { reportCreateNewLogoClick } from '../../../../../shared/reportEvents/bigQuery';
import monitor, { TRACKING_PAGE_NAMES } from '../../../../../service/bigquery';
import { AVATAR_PROPS } from '../../../../../shared/reportEvents/bigQuery/reportCreateNewLogoClick/constants';
import useOnHeaderNewLogoLinkClick from '../../../../../shared/hooks/useOnHeaderNewLogoLinkClick';
import { raiseToast, TOAST_TYPES } from '../../../../../shared/utils/raiseToast';
import * as userSavedProjects from '../../../../../shared/api/userSavedProjects';
import { handleError } from '../../../../../shared/api/errors';
import { APP_NAME } from '../../constants';
import { buyVariation } from '../../Navigation/helpers';

const { setData, reportPageView, reportAddToFavorites, reportFreeVariation, reportRemoveFromFavorites } =
    makersExperience.chooseVariation;

const { Location } = reportAddToFavorites;

const useChooseVariation = ({
    userId,
    projectId,
    briefId,
    brandName,
    slogan,
    draftsVariations,
    ordersVariations,
    suggestedVariations,
    project,
    sellerData,
    briefData,
    simplifiedPackageType,
    currentUser,
    userSavedProject,
    selectedVariationId: initialSelectedVariationId,
    availablePackages,
    hasFreeVariations,
    httpRequest,
    source,
}) => {
    const { isMobile: uaIsMobile, isTouch } = useUserAgentContext();

    const [isDesktopSize, setIsDesktopSize] = useState(!uaIsMobile);
    const [isTabletSize, setIsTabletSize] = useState(false);
    const [isMobile, setIsMobile] = useState(uaIsMobile);

    const isDesktopSizeMedia = useMediaQuery({
        query: `(min-width: ${breakpointMd}px)`,
    });

    const isTabletSizeMedia = useMediaQuery({
        query: `(min-width: ${breakpointSm}px) and (max-width: ${breakpointReverseMd}px)`,
    });

    const isMobileSizeMedia = useMediaQuery({
        query: `(max-width: ${breakpointReverseSm}px)`,
    });

    useEffect(() => {
        setIsDesktopSize(isDesktopSizeMedia);
        setIsTabletSize(isTabletSizeMedia);
        setIsMobile(uaIsMobile || isMobileSizeMedia);
    }, [isDesktopSizeMedia, isTabletSizeMedia, isMobileSizeMedia, uaIsMobile]);

    const allVariations = useMemo(
        () => [
            ...draftsVariations.map((variation) => ({
                ...variation,
                type: VARIATION_TYPE.DRAFT,
            })),
            ...ordersVariations.map((variation) => ({
                ...variation,
                type: VARIATION_TYPE.ORDERED,
            })),
            ...suggestedVariations.map((variation) => ({
                ...setVariationTextFields({ variation, brandName, slogan }),
                type: VARIATION_TYPE.SUGGESTED,
            })),
        ],
        [brandName, slogan, suggestedVariations, draftsVariations, ordersVariations]
    );
    const [mainSampleRendered, setMainSampleRendered] = useState(false);
    const [isPurchasing, setIsPurchasing] = useState(false);
    const [projectIsSaved, setProjectIsSaved] = useState(!!userSavedProject);
    const [userSavedProjectId, setUserSavedProjectId] = useState(userSavedProject?.id || null);
    const [selectedVariationId, setSelectedVariationId] = useState(initialSelectedVariationId);
    const isProjectOwner = currentUser?.id === project.user_id;

    const contextValue = useMemo(
        () => ({
            userId,
            projectId,
            briefId,
            brandName,
            slogan,
            isProjectOwner,
            variations: allVariations,
            selectedVariationId,
            setSelectedVariationId,
            hasFreeVariations,
        }),
        [
            userId,
            projectId,
            briefId,
            brandName,
            slogan,
            isProjectOwner,
            allVariations,
            selectedVariationId,
            setSelectedVariationId,
            hasFreeVariations,
        ]
    );

    useEffectOnce(() => {
        setData({
            projectId,
            variationId: selectedVariationId,
            sellerId: sellerData.id,
            brief: {
                ...briefData,
                brandName,
                slogan,
            },
        });
        reportPageView();
        monitor.trackPageView({ httpRequest });
    });

    useTopLevelSource(source);

    const onNewLogoLinkClick = () => {
        reportCreateNewLogoClick({
            pageName: TRACKING_PAGE_NAMES.CHOOSE_VARIATION,
            ...AVATAR_PROPS,
        });
    };

    useOnHeaderNewLogoLinkClick(onNewLogoLinkClick);

    const saveProject = async (projectId, variationId) => {
        const data = {
            projectId,
            variationId,
            briefId,
            brandName,
            slogan,
        };

        const id = await userSavedProjects.create(data);
        setUserSavedProjectId(id);
    };

    const unsaveProject = async (userSavedId) => {
        await userSavedProjects.delete(userSavedId);
        setUserSavedProjectId(null);
    };

    const changeSavedProject = async (variationId, status) => {
        if (!currentUser) {
            try {
                await triggerSignIn({
                    modalOptions: {
                        triggerType: 'blocked_action',
                    },
                });
            } catch {
                window.location = pathfinder('login_page');
            }
        } else {
            const variationPosition = allVariations.findIndex(({ id }) => id === selectedVariationId) + 1;
            status
                ? reportAddToFavorites(variationPosition, Location.VARIATIONS)
                : reportRemoveFromFavorites(variationPosition, Location.VARIATIONS);

            try {
                await (status ? saveProject(projectId, variationId) : unsaveProject(userSavedProjectId));
                setProjectIsSaved(status);
                return true;
            } catch (err) {
                handleError(APP_NAME, err.message, err);
            }
        }
        return false;
    };

    const handlePurchase = async () => {
        if (!userId) {
            try {
                await triggerSignIn({
                    modalOptions: {
                        triggerType: 'blocked_action',
                        blockedActionType: 'checkout',
                    },
                });
            } catch {
                window.location = pathfinder('login_page');
            }
            return;
        }

        setIsPurchasing(true);
        hasFreeVariations && reportFreeVariation(simplifiedPackageType);
        try {
            const isDraftVariation = draftsVariations && draftsVariations.some(({ id }) => id === selectedVariationId);

            const success = await buyVariation({
                isProjectOwner,
                projectId,
                brandName,
                slogan,
                briefId,
                refCtxId: monitor.context.refCtxId,
                selectedVariationId,
                availablePackages,
                isFreeVariation: hasFreeVariations,
                isDraftVariation,
            });

            if (!success) {
                setIsPurchasing(false);
                raiseToast({
                    type: TOAST_TYPES.ERROR,
                    content: <I18n k="logo_maker_perseus.choose_variation.navigation.free_variation_purchase_failed" />,
                });
            }
        } catch {
            setIsPurchasing(false);
            raiseToast({
                type: TOAST_TYPES.ERROR,
                content: <I18n k="logo_maker_perseus.general.something_went_wrong" />,
            });
        }
    };

    return {
        isTouch,
        isMobile,
        uaIsMobile,
        isDesktopSize,
        isTabletSize,
        isPurchasing,
        projectIsSaved,
        isProjectOwner,
        mainSampleRendered,
        handlePurchase,
        changeSavedProject,
        setMainSampleRendered,
        contextValue,
    };
};

export default useChooseVariation;
